// Central capability map for the admin panel.
//
// Derived from the backend route gates and service-level checks
// (src/startup/startServer.ts decorators: isAdmin = platform roles,
// isPlatformAdmin, isSupervisor, isAccountant; plus service rules such as
// fund-request review being platform_admin only and fund-request creation
// being supervisor only). The UI uses these to decide what to SHOW; the API
// remains the authority for what is ALLOWED.

import { ROLE } from "../utils/access";

const PA = ROLE.PLATFORM_ADMIN;
const PU = ROLE.PLATFORM_USER;
const SUP = ROLE.SUPERVISOR;
const ACC = ROLE.ACCOUNTANT;

const PLATFORM = [PA, PU];
const ADMINISTRATIVE = [PA, PU, SUP];
const REPORTING = [PA, PU, SUP, ACC];

export const CAPABILITY = Object.freeze({
  PANEL_ACCESS: "panel.access",
  DASHBOARD_VIEW: "dashboard.view",
  REPORTS_VIEW: "reports.view",
  REPORTS_FILTER_AGENCY: "reports.filterAgency",
  USERS_VIEW: "users.view",
  USERS_MANAGE: "users.manage",
  AGENCIES_VIEW: "agencies.view",
  AGENCIES_MANAGE: "agencies.manage",
  AGENCY_BALANCE_ADJUST: "agencies.balanceAdjust",
  AIRLINES_VIEW: "airlines.view",
  AIRLINES_MANAGE: "airlines.manage",
  CURRENCIES_VIEW: "currencies.view",
  CURRENCIES_MANAGE: "currencies.manage",
  ADS_MANAGE: "ads.manage",
  // GET /ads/settings is isAdmin; PUT /ads/settings (the global "ads on
  // e-tickets" switch) is isPlatformAdmin.
  ADS_GLOBAL_TOGGLE: "ads.globalToggle",
  TRANSACTIONS_VIEW: "transactions.view",
  TOPUPS_VIEW: "topUps.view",
  TOPUPS_PLATFORM_SCOPE: "topUps.platformScope",
  FUND_REQUESTS_VIEW: "fundRequests.view",
  FUND_REQUESTS_REQUEST: "fundRequests.request",
  FUND_REQUESTS_REVIEW: "fundRequests.review",
  AUTOBOOK_VIEW: "autoBook.view",
  LOGS_VIEW: "logs.view",
  // GET /logs/errors and /logs/provider (+ their exports) are platform-only
  // (route guard isAdmin); supervisors read only their agency's action logs.
  LOGS_ERRORS_VIEW: "logs.errors.view",
  LOGS_PROVIDER_VIEW: "logs.provider.view",
  LOGS_FILTER_AGENCY: "logs.filterAgency",
  // GET /logs/<category>/export follows the list gate of each category.
  LOGS_EXPORT: "logs.export",
  PLATFORM_SETTINGS_VIEW: "platformSettings.view",
  PLATFORM_COMMISSION_EDIT: "platformSettings.commissionEdit",
  MOAMALAT_FEE_EDIT: "platformSettings.moamalatFeeEdit",
  // PUT /platform/session-settings is platform_admin only (isPlatformAdmin).
  AGENT_PORTAL_SESSION_EDIT: "platformSettings.agentPortalSessionEdit",
  // GET /fx-rates is isAdmin (platform roles); POST /fx-rates and
  // /fx-rates/:id/retire are isPlatformAdmin.
  FX_RATES_VIEW: "fxRates.view",
  FX_RATES_MANAGE: "fxRates.manage",
  // Service-fee rules ("Financial rules"): reads isAdmin, every write
  // (platform defaults and agency overrides) isPlatformAdmin.
  SERVICE_FEES_EDIT: "serviceFees.edit",
  // PUT /agency/accounts/:accountId/status is isPlatformAdmin; the status
  // history (GET .../status-changes) is isAdmin.
  AGENCY_ACCOUNT_STATUS_EDIT: "agencies.accountStatusEdit",
  // Airline credit: GET /platform/airline-credit and its alert settings are
  // isAdmin; POST .../refresh and PUT .../alert-settings are isPlatformAdmin.
  AIRLINE_CREDIT_VIEW: "airlineCredit.view",
  AIRLINE_CREDIT_REFRESH: "airlineCredit.refresh",
  AIRLINE_CREDIT_ALERT_EDIT: "platformSettings.airlineCreditAlertEdit",
  // POST /platform/airline-credit/airlines/:airlineId/pause and .../resume
  // are isPlatformAdmin.
  AIRLINE_TICKETING_PAUSE: "airlineCredit.ticketingPause",
  // GET /reports/platform-income (Dashboard income cards) is isPlatformAdmin.
  PLATFORM_INCOME_VIEW: "dashboard.platformIncome",
  PROFILE_PASSWORD: "profile.password",
});

const CAPABILITY_ROLES = Object.freeze({
  [CAPABILITY.PANEL_ACCESS]: REPORTING,
  [CAPABILITY.DASHBOARD_VIEW]: REPORTING,
  [CAPABILITY.REPORTS_VIEW]: REPORTING,
  [CAPABILITY.REPORTS_FILTER_AGENCY]: PLATFORM,
  [CAPABILITY.USERS_VIEW]: ADMINISTRATIVE,
  [CAPABILITY.USERS_MANAGE]: ADMINISTRATIVE,
  [CAPABILITY.AGENCIES_VIEW]: PLATFORM,
  [CAPABILITY.AGENCIES_MANAGE]: PLATFORM,
  [CAPABILITY.AGENCY_BALANCE_ADJUST]: [PA],
  [CAPABILITY.AIRLINES_VIEW]: PLATFORM,
  [CAPABILITY.AIRLINES_MANAGE]: PLATFORM,
  [CAPABILITY.CURRENCIES_VIEW]: PLATFORM,
  [CAPABILITY.CURRENCIES_MANAGE]: [PA],
  [CAPABILITY.ADS_MANAGE]: PLATFORM,
  [CAPABILITY.ADS_GLOBAL_TOGGLE]: [PA],
  [CAPABILITY.TRANSACTIONS_VIEW]: PLATFORM,
  [CAPABILITY.TOPUPS_VIEW]: ADMINISTRATIVE,
  [CAPABILITY.TOPUPS_PLATFORM_SCOPE]: PLATFORM,
  [CAPABILITY.FUND_REQUESTS_VIEW]: [PA, SUP],
  [CAPABILITY.FUND_REQUESTS_REQUEST]: [SUP],
  [CAPABILITY.FUND_REQUESTS_REVIEW]: [PA],
  [CAPABILITY.AUTOBOOK_VIEW]: ADMINISTRATIVE,
  [CAPABILITY.LOGS_VIEW]: ADMINISTRATIVE,
  [CAPABILITY.LOGS_ERRORS_VIEW]: PLATFORM,
  [CAPABILITY.LOGS_PROVIDER_VIEW]: PLATFORM,
  [CAPABILITY.LOGS_FILTER_AGENCY]: PLATFORM,
  [CAPABILITY.LOGS_EXPORT]: ADMINISTRATIVE,
  [CAPABILITY.PLATFORM_SETTINGS_VIEW]: PLATFORM,
  [CAPABILITY.PLATFORM_COMMISSION_EDIT]: [PA],
  [CAPABILITY.MOAMALAT_FEE_EDIT]: [PA],
  [CAPABILITY.AGENT_PORTAL_SESSION_EDIT]: [PA],
  [CAPABILITY.FX_RATES_VIEW]: PLATFORM,
  [CAPABILITY.FX_RATES_MANAGE]: [PA],
  [CAPABILITY.SERVICE_FEES_EDIT]: [PA],
  [CAPABILITY.AGENCY_ACCOUNT_STATUS_EDIT]: [PA],
  [CAPABILITY.AIRLINE_CREDIT_VIEW]: PLATFORM,
  [CAPABILITY.AIRLINE_CREDIT_REFRESH]: [PA],
  [CAPABILITY.AIRLINE_CREDIT_ALERT_EDIT]: [PA],
  [CAPABILITY.AIRLINE_TICKETING_PAUSE]: [PA],
  [CAPABILITY.PLATFORM_INCOME_VIEW]: [PA],
  [CAPABILITY.PROFILE_PASSWORD]: REPORTING,
});

export const can = (role, capability) => {
  if (!role || !capability) {
    return false;
  }
  const roles = CAPABILITY_ROLES[capability];
  return Array.isArray(roles) && roles.includes(role);
};

export const canAll = (role, capabilities = []) =>
  capabilities.every((capability) => can(role, capability));

export const canAny = (role, capabilities = []) =>
  capabilities.some((capability) => can(role, capability));

export const rolesFor = (capability) => CAPABILITY_ROLES[capability] || [];

export const getCapabilityMatrix = () => CAPABILITY_ROLES;
